import { VIEW_WIDGET_KEY, ViewCmdType } from './IViewWidget.js';
import { Widget } from '../core/Widget.js';

/**
 * Хелпер для работы с ViewWidget.
 */
class ViewWidgetHelper {
    /** Получение view на элементе */
    static getOnByName(viewHost) {
        return Widget.getOnByName(viewHost, VIEW_WIDGET_KEY);
    }
    /** Получение ближайшего view к элементу */
    static closestOnByName(element) {
        return Widget.closestOnByName(element, VIEW_WIDGET_KEY, 'im-view');
    }
    /** Получение маски, ограничивающей контент view. */
    static getUiNotMask(viewHost) {
        if (!viewHost)
            return '';
        return Widget.getOnByName(viewHost, VIEW_WIDGET_KEY)?.getUiNotMask() ?? '';
    }
    /**
     * Закрыть указанное view.
     * Эквивалент легаси: $(...).imView('close')
     *
     * @param viewHost .im-view (или вложенный элемент)
     * @param viewOnly скрыть только содержимое без удаления
     * @param dontAsc  флаг "не подниматься" при закрытии таба
     */
    static close(viewHost, viewOnly = false, dontAsc = false) {
        if (!viewHost)
            return;
        Widget.getOnByName(viewHost, VIEW_WIDGET_KEY)?.close(viewOnly, dontAsc);
    }
    /**
     * Выполнить строку UI-команд на ViewWidget.
     *
     * @param viewHost .im-view (или вложенный элемент)
     * @param el        целевой элемент (обычно sourceEl)
     * @param cmdString строка команд
     * @returns true если команда вернула preventDefault, иначе false
     */
    static execCmdString(viewHost, el, cmdString) {
        if (!viewHost)
            return false;
        const view = this.closestOnByName(viewHost);
        return view ? view.execCmdString(el, cmdString) : false;
    }
    /**
     * 🚪 Точка входа: обработать ОДНУ команду сервера через новый ServerCmd-слой.
     *
     * @param viewHost .im-view (или вложенный элемент)
     * @param cmd      одна команда сервера (ServerCmdData)
     * @param uiNotMask маска исключения; по умолчанию берётся из ViewWidget
     * @returns true, если команда была успешно адаптирована и выполнена; иначе false
     */
    static execServerCmd(viewHost, cmd, uiNotMask) {
        if (!viewHost || !cmd)
            return false;
        const view = Widget.getOnByName(viewHost, VIEW_WIDGET_KEY);
        return view ? view.execServerCmd(cmd, uiNotMask) : false;
    }
    /**
     * Установить состояние busy (глобальное или по кнопке).
     */
    static modeBusy(viewHost, btn) {
        if (!viewHost)
            return;
        Widget.getOnByName(viewHost, VIEW_WIDGET_KEY)?.modeBusy(btn);
    }
    /**
     * Сбросить состояние busy (глобальное или по кнопке).
     */
    static modeNormal(viewHost, btn) {
        if (!viewHost)
            return;
        Widget.getOnByName(viewHost, VIEW_WIDGET_KEY)?.modeNormal(btn);
    }
    /**
     * Обновить состояние командной панели.
     */
    static updatePanelState(viewHost) {
        if (!viewHost)
            return;
        Widget.getOnByName(viewHost, VIEW_WIDGET_KEY)?.updatePanelState();
    }
    /**
     * Совместимый с легаси вызов серверной команды.
     * Полностью заменяет старый jQuery-метод doJson(data, callback, onerror, setBusy).
     *
     * @param viewHost .im-view (или вложенный элемент)
     * @param data      объект запроса к серверу (сериализуется как есть)
     * @param callback  вызовется при явной команде "callback" ИЛИ один раз в конце,
     *                  если не было ошибок и явных callback-команд
     * @param onerror   вызовется при ошибке или при команде "error"
     * @param setBusy   показывать busy на время запроса (по умолчанию true)
     */
    static doJson(viewHost, data, callback, onerror, setBusy = true) {
        if (!viewHost)
            return;
        Widget.getOnByName(viewHost, VIEW_WIDGET_KEY)
            ?.doJson(data, callback, onerror, setBusy);
    }
    /**
     * Проверка полноты заполнения формы (data-required, data-valid).
     * Совместимый вызов нового контроллера валидации.
     *
     * @param viewHost .im-view (или вложенный элемент)
     * @param btn      кнопка, по которой идёт проверка
     * @returns true если можно выполнять действие; false если есть нарушения
     */
    static checkButtonRequires(viewHost, btn) {
        if (!viewHost || !btn)
            return false;
        const view = Widget.getOnByName(viewHost, VIEW_WIDGET_KEY);
        return view?.validation.checkButtonRequires(btn) ?? true;
    }
    /**
    * Вызов jQuery imView('execCmd', …).
    * @param el Элемент, от имени которого вызывается команда
    * @param a Объект аргументов
    * @param targetView Явный view (если не указан — ищется вверх по DOM)
    * @returns 2026-08-16. true — запрос отправлен и колбэки будут вызваны;
    *          false — команда не запущена (не найден view либо не пройден data-required)
    */
    static ExecCmd(el, a, targetView) {
        const viewEl = this.closestOnByName(targetView ?? el);
        if (!viewEl) {
            console.error("View не найден для элемента:", el);
            return false;
        }
        if (!a.cmdType)
            a.cmdType = ViewCmdType.itemcmd;
        const { cmdType, cmd, options, onSuccess, onError, appendValues, setBusy } = a;
        return viewEl.execCmd(el, cmdType, cmd, options, onSuccess, onError, appendValues, setBusy);
    }
    /**
    * Вызов перезагрузки view через jQuery imView('reload', ...).
    *
    * @param el Любой вложенный элемент .im-view или сама .im-view
    * @param args Параметры перезагрузки
    */
    static reload(el, args = {}) {
        const { itemKeysToReload, onCallback, onError, reloadType = ViewCmdType.reload, options = {} } = args;
        const viewEl = this.closestOnByName(el);
        if (!viewEl) {
            console.error('imView reload: .im-view не найден для', el);
            return;
        }
        // Сигнатура полностью соответствует старому API:
        // imView('reload', itemKeysToReload, onCallback, onError, reloadType, options)
        viewEl.reload(itemKeysToReload, onCallback, onError, reloadType, options);
    }
    /**
     * Вызов jQuery imView('onChangeValue', …) для item-команд.
     * Сигнатура соответствует legacy:
     * onChangeValue(el, cmd, options, 'itemcmd', undefined, onSuccess, onError)
     *
     * @param el Элемент, от имени которого вызывается команда
     * @param a Объект аргументов
     * @param targetView Явный view (если не указан — ищется вверх по DOM)
     */
    static ExecItemCmd(el, a, targetView) {
        // По сути это одна и таже команда что и onChangeValue
        a.cmdType = ViewCmdType.itemcmd;
        this.onChangeValue(el, a, targetView);
    }
    /**
     * Вызов jQuery imView('onChangeValue', …) для item-команд.
     * Сигнатура соответствует legacy:
     * onChangeValue(el, cmd, options, cmdType, undefined, onSuccess, onError)
     *
     * @param el Элемент, от имени которого вызывается команда
     * @param a Объект аргументов
     * @param targetView Явный view (если не указан — ищется вверх по DOM)
     */
    static onChangeValue(el, a, targetView) {
        const viewEl = this.closestOnByName(targetView ?? el);
        if (!viewEl) {
            console.error('View не найден для элемента:', el);
            return;
        }
        const { cmd, options = {}, onSuccess, onError, appled, cmdType } = a;
        viewEl.onChangeValue(el, cmd, options, cmdType ?? ViewCmdType.itemcmd, appled, onSuccess, onError);
    }
}

export { ViewWidgetHelper };
